'use client';

import { AuthPageControls } from '@/components/auth/auth-page-controls';
import { GridPattern } from '@/components/ui/grid-pattern';
import { cn } from '@/lib/utils';

/**
 * Shared wrapper for all auth pages (login, forgot-password, reset-password, etc.)
 * Provides consistent background, centering, and card styling.
 * Includes language/theme controls in the top-right corner.
 */
export function AuthPageWrapper({
  children,
  maxWidth = 'max-w-sm',
}: {
  children: React.ReactNode;
  maxWidth?: string;
}) {
  return (
    <section className="relative min-h-dvh bg-muted">
      <AuthPageControls />
      <GridPattern
        width={30}
        height={30}
        strokeDasharray="4 4"
        className={cn(
          'fixed inset-0 size-full',
          'mask-[radial-gradient(700px_circle_at_center,white,transparent)]',
          'fill-muted-foreground/20 stroke-muted-foreground/20 dark:fill-muted-foreground/20 dark:stroke-muted-foreground/20',
        )}
      />
      <div className="relative flex min-h-dvh items-center justify-center px-4 py-12">
        <div
          className={cn(
            'flex w-full flex-col items-center gap-y-8 rounded-xl border border-border bg-white px-6 py-10 shadow-sm sm:px-8 sm:py-12',
            'dark:border-muted-foreground/20 dark:bg-card dark:shadow-2xl dark:ring-1 dark:ring-white/10',
            maxWidth,
          )}
        >
          {children}
        </div>
      </div>
    </section>
  );
}

/**
 * Shared logo + title header for auth pages
 */
export function AuthPageLogo({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center gap-y-2">
      <div className="flex items-center gap-1">
        <span className="text-4xl font-semibold">
          <span
            className="inline-block rotate-180 relative"
            style={{ top: '0.15em', transformOrigin: 'center center' }}
          >
            v
          </span>
          rkyv
        </span>
      </div>
      <h1 className="text-xl font-medium text-muted-foreground">{title}</h1>
    </div>
  );
}
